import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2, Clock, UserCheck, Mail, MailCheck, CheckCircle2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useStepUpAuth } from "@/hooks/useStepUpAuth";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

import { RedactedEmail } from "./RedactedEmail";
import { StepUpAuthDialog } from "@/components/security/StepUpAuthDialog";

interface WaitlistUser {
  id: string;
  email: string;
  created_at: string;
  role_created_at: string;
  email_verified: boolean;
  queue_position?: number;
}

interface ApprovedUser {
  id: string;
  email: string;
  approved_at: string;
  email_verified: boolean;
}

const redactEmail = (email: string): string => {
  if (!email || !email.includes("@")) return email;
  
  const [username, domain] = email.split("@");
  if (username.length <= 2) {
    return `${"*".repeat(username.length)}@${domain}`;
  }
  
  const visiblePart = username.slice(-2);
  const hiddenPart = "*".repeat(username.length - 2);
  return `${hiddenPart}${visiblePart}@${domain}`;
};

type UserFilter = "all" | "verified" | "unverified";

interface WaitlistManagementProps {
  waitlistUsers: WaitlistUser[];
  approvedUsers: ApprovedUser[];
}

export const WaitlistManagement = ({ 
  waitlistUsers, 
  approvedUsers,
}: WaitlistManagementProps) => {
  const [waitlistFilter, setWaitlistFilter] = useState<UserFilter>("all");
  const [approvedFilter, setApprovedFilter] = useState<UserFilter>("all");
  const { requireStepUp, showStepUpDialog, setShowStepUpDialog, handleVerified, currentActionType, stepUpData } = useStepUpAuth();
  const { invokeAdmin } = useAdminEdgeFunction();
  const queryClient = useQueryClient();
  const [pendingApprovalUserId, setPendingApprovalUserId] = useState<string | null>(null);

  const approveUserMutation = useMutation({
    mutationFn: (userId: string) => invokeAdmin("approve-user", { body: { userId }, showErrorToast: true }),
    onSuccess: () => {
      toast.success("User approved successfully! Approval email sent.");
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onSettled: () => setPendingApprovalUserId(null),
  });

  const handleApproveUser = async (userId: string) => {
    setPendingApprovalUserId(userId);
    await requireStepUp('approve_user', async () => {
      approveUserMutation.mutate(userId);
    });
  };

  const verifyUserEmailMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.rpc("admin_verify_user_email", { target_user_id: userId });
      if (error) throw error;
      if (data && !(data as any).success) throw new Error((data as any).error || "Failed to verify user");
    },
    onSuccess: () => {
      toast.success("User email verified successfully!");
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (error: any) => {
      console.error("Error verifying user:", error);
      toast.error(error.message || "Failed to verify user");
    },
  });

  const handleVerifyUserEmail = async (userId: string) => {
    verifyUserEmailMutation.mutate(userId);
  };

  const sendReminderMutation = useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string }) =>
      invokeAdmin("send-verification-reminder", {
        body: { userId, email },
        showErrorToast: true,
      }),
    onSuccess: () => {
      toast.success("Verification reminder sent successfully");
    },
    onError: (error: any) => {
      console.error("Error sending reminder:", error);
      // Error toast already shown by hook if showErrorToast is true
    },
  });

  const handleSendReminder = async (userId: string, email: string) => {
    sendReminderMutation.mutate({ userId, email });
  };

  const filteredWaitlistUsers = waitlistUsers.filter(user => {
    if (waitlistFilter === "verified") return user.email_verified;
    if (waitlistFilter === "unverified") return !user.email_verified;
    return true;
  });

  const filteredApprovedUsers = approvedUsers.filter(user => {
    if (approvedFilter === "verified") return user.email_verified;
    if (approvedFilter === "unverified") return !user.email_verified;
    return true;
  });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Waitlist Queue */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Waitlist Queue
              </CardTitle>
              <CardDescription>
                Users waiting for approval (ordered by signup time)
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={waitlistFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setWaitlistFilter("all")}
              >
                All
              </Button>
              <Button
                variant={waitlistFilter === "verified" ? "default" : "outline"}
                size="sm"
                onClick={() => setWaitlistFilter("verified")}
              >
                Verified
              </Button>
              <Button
                variant={waitlistFilter === "unverified" ? "default" : "outline"}
                size="sm"
                onClick={() => setWaitlistFilter("unverified")}
              >
                Unverified
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredWaitlistUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No {waitlistFilter !== "all" ? waitlistFilter : ""} users in waitlist
            </p>
          ) : (
            <div className="space-y-3">
              {filteredWaitlistUsers.map((user, index) => (
                <div
                  key={user.id}
                  className={`flex items-center justify-between gap-2 p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors ${
                    !user.email_verified ? "border-l-4 border-l-yellow-500" : ""
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Badge variant="outline" className="w-10 text-center shrink-0">
                      #{user.queue_position || index + 1}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <RedactedEmail userId={user.id} redactedEmail={redactEmail(user.email)} />
                        {user.email_verified ? (
                          <MailCheck className="h-4 w-4 text-green-600 shrink-0" />
                        ) : (
                          <Mail className="h-4 w-4 text-yellow-600 shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(user.role_created_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {!user.email_verified && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleVerifyUserEmail(user.id)}
                          disabled={verifyUserEmailMutation.isPending && verifyUserEmailMutation.variables === user.id}
                          title="Manually verify email"
                        >
                          {verifyUserEmailMutation.isPending && verifyUserEmailMutation.variables === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MailCheck className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendReminder(user.id, user.email)}
                          disabled={sendReminderMutation.isPending && sendReminderMutation.variables?.userId === user.id}
                          title="Send reminder email"
                        >
                          {sendReminderMutation.isPending && sendReminderMutation.variables?.userId === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleApproveUser(user.id)}
                      disabled={approveUserMutation.isPending && approveUserMutation.variables === user.id}
                    >
                      {approveUserMutation.isPending && approveUserMutation.variables === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Approve
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recently Approved */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                Recently Approved
              </CardTitle>
              <CardDescription>Users approved in the last 7 days</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={approvedFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setApprovedFilter("all")}
              >
                All
              </Button>
              <Button
                variant={approvedFilter === "verified" ? "default" : "outline"}
                size="sm"
                onClick={() => setApprovedFilter("verified")}
              >
                Verified
              </Button>
              <Button
                variant={approvedFilter === "unverified" ? "default" : "outline"}
                size="sm"
                onClick={() => setApprovedFilter("unverified")}
              >
                Unverified
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredApprovedUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No {approvedFilter !== "all" ? approvedFilter : ""} recent approvals
            </p>
          ) : (
            <div className="space-y-3">
              {filteredApprovedUsers.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center justify-between p-3 rounded-lg border bg-card ${
                    !user.email_verified ? "border-l-4 border-l-yellow-500" : ""
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <RedactedEmail userId={user.id} redactedEmail={redactEmail(user.email)} />
                        {user.email_verified ? (
                          <MailCheck className="h-4 w-4 text-green-600 shrink-0" />
                        ) : (
                          <Mail className="h-4 w-4 text-yellow-600 shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Approved {format(new Date(user.approved_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!user.email_verified && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleVerifyUserEmail(user.id)}
                          disabled={verifyUserEmailMutation.isPending && verifyUserEmailMutation.variables === user.id}
                          title="Manually verify email"
                        >
                          {verifyUserEmailMutation.isPending && verifyUserEmailMutation.variables === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MailCheck className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendReminder(user.id, user.email)}
                          disabled={sendReminderMutation.isPending && sendReminderMutation.variables?.userId === user.id}
                          title="Send reminder email"
                        >
                          {sendReminderMutation.isPending && sendReminderMutation.variables?.userId === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                        </Button>
                      </>
                    )}
                    <Badge variant={user.email_verified ? "secondary" : "outline"} className="ml-2">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Approved
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step-Up Verification Dialog for Critical Actions */}
      <StepUpAuthDialog
        open={showStepUpDialog}
        onOpenChange={setShowStepUpDialog}
        onVerified={handleVerified}
        actionType={currentActionType}
        reason={stepUpData?.reason}
        anomalyScore={stepUpData?.anomaly_score}
      />
    </div>
  );
};
